'use client'

import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '@/contexts/AuthContext'

const API_URL = process.env.NEXT_PUBLIC_API_URL

interface FeatureAccessState {
  has_access: boolean
  on_waitlist: boolean
}

const FEATURE_ACCESS_CACHE_TTL = 120_000

type FeatureMap = Record<string, FeatureAccessState>

// The backend exposes `GET /v1/feature-access` which returns ALL of a user's
// feature flags in one response. We fetch that list once per user (coalesced +
// cached) and serve every flag lookup from it, instead of firing a separate
// request per flag. Namespaced `app:*` flags not present in the list fall back
// to the per-feature endpoint.
const featureListCache = new Map<string, { map: FeatureMap; at: number }>()
const featureListInFlight = new Map<string, Promise<FeatureMap>>()
const featureSingleInFlight = new Map<string, Promise<FeatureAccessState>>()

function resolveFeatureList(userId: string): Promise<FeatureMap> {
  const cached = featureListCache.get(userId)
  if (cached && Date.now() - cached.at < FEATURE_ACCESS_CACHE_TTL) {
    return Promise.resolve(cached.map)
  }
  const existing = featureListInFlight.get(userId)
  if (existing) return existing

  const flight = (async (): Promise<FeatureMap> => {
    try {
      const res = await fetch(`${API_URL}/v1/feature-access`, { credentials: 'include' })
      const map: FeatureMap = {}
      if (res.ok) {
        const rows = (await res.json()) as Array<{
          feature?: string
          has_access?: boolean
          on_waitlist?: boolean
        }>
        if (Array.isArray(rows)) {
          for (const row of rows) {
            if (row && typeof row.feature === 'string') {
              map[row.feature] = {
                has_access: Boolean(row.has_access),
                on_waitlist: Boolean(row.on_waitlist),
              }
            }
          }
        }
      }
      featureListCache.set(userId, { map, at: Date.now() })
      return map
    } catch {
      const map: FeatureMap = {}
      featureListCache.set(userId, { map, at: Date.now() })
      return map
    } finally {
      featureListInFlight.delete(userId)
    }
  })()
  featureListInFlight.set(userId, flight)
  return flight
}

function resolveSingleFeature(feature: string, userId: string): Promise<FeatureAccessState> {
  const cacheKey = `${userId}:${feature}`
  const existing = featureSingleInFlight.get(cacheKey)
  if (existing) return existing
  const flight = (async (): Promise<FeatureAccessState> => {
    try {
      const res = await fetch(`${API_URL}/v1/feature-access/${encodeURIComponent(feature)}`, {
        credentials: 'include',
      })
      return res.ok ? await res.json() : { has_access: true, on_waitlist: false }
    } catch {
      return { has_access: true, on_waitlist: false }
    } finally {
      featureSingleInFlight.delete(cacheKey)
    }
  })()
  featureSingleInFlight.set(cacheKey, flight)
  return flight
}

export async function resolveFeatureAccess(feature: string, userId: string): Promise<FeatureAccessState> {
  const map = await resolveFeatureList(userId)
  if (feature in map) return map[feature]
  // Not in the list (e.g. a namespaced `app:*` flag) — fall back to the
  // per-feature endpoint, defaulting to allow on failure to match prior behavior.
  return resolveSingleFeature(feature, userId)
}

/** Invalidate the feature-access cache (e.g. after admin changes). */
export function invalidateFeatureAccess(): void {
  featureListCache.clear()
  featureListInFlight.clear()
  featureSingleInFlight.clear()
}

export function useFeatureAccess(feature: string) {
  const { user } = useAuth()
  const [state, setState] = useState<FeatureAccessState | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const check = useCallback(async () => {
    if (!user || !API_URL) {
      setState(null)
      setIsLoading(false)
      return
    }
    try {
      setState(await resolveFeatureAccess(feature, user.id))
    } finally {
      setIsLoading(false)
    }
  }, [user, feature])

  useEffect(() => {
    check()
  }, [check])

  return {
    hasAccess: state?.has_access ?? true,
    onWaitlist: state?.on_waitlist ?? false,
    isLoading,
    refetch: check,
  }
}
